import {
	Button,
	type ColumnDef,
	DataTable,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	useUrlState,
} from "@monbits/ui";
import { ChevronRight, Clock, Wallet } from "lucide-react";
import * as React from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import type { PoolWithdrawal } from "src/providers/pool-withdrawal-provider";
import { usePoolWithdrawals } from "src/queries/pool-withdrawal-queries";
import { statusConfig } from "./status-config";

type StatusFilterValue = PoolWithdrawal["status"] | "all";

const STATUS_OPTIONS: Array<{ value: StatusFilterValue; label: string }> = [
	{ value: "all", label: "All Statuses" },
	{ value: "pending_approval", label: "Pending Approval" },
	{ value: "approved", label: "Approved" },
	{ value: "processing", label: "Processing" },
	{ value: "completed", label: "Completed" },
	{ value: "failed", label: "Failed" },
	{ value: "rejected", label: "Rejected" },
	{ value: "cancelled", label: "Cancelled" },
];

const truncateAddress = (addr: string) =>
	`${addr.slice(0, 8)}...${addr.slice(-6)}`;

const columns: ColumnDef<PoolWithdrawal>[] = [
	{
		id: "id",
		header: "ID",
		cell: (pw) => <span className="font-medium">#{pw.id}</span>,
	},
	{
		id: "network",
		header: "Network",
		cell: (pw) => (
			<div className="flex items-center gap-2">
				<CryptoIcon ticker={pw.blockchain} className="h-5 w-5" />
				<span>{pw.blockchain}</span>
			</div>
		),
	},
	{
		id: "status",
		header: "Status",
		cell: (pw) => {
			const config = statusConfig[pw.status] ?? {
				label: pw.status,
				type: "default" as const,
				icon: Clock,
			};
			return (
				<StatusBadge type={config.type} icon={config.icon}>
					{config.label}
				</StatusBadge>
			);
		},
	},
	{
		id: "amount",
		header: "Amount",
		cell: (pw) => (
			<div className="flex items-center gap-2">
				<CryptoIcon ticker={pw.currency} className="h-4 w-4" />
				<span className="font-mono">{pw.amount}</span>
			</div>
		),
	},
	{
		id: "from",
		header: "From",
		cell: (pw) => (
			<span className="font-mono text-sm">
				{truncateAddress(pw.walletAddress)}
			</span>
		),
	},
	{
		id: "to",
		header: "To",
		cell: (pw) => (
			<span className="font-mono text-sm">
				{truncateAddress(pw.recipientAddress)}
			</span>
		),
	},
	{
		id: "created",
		header: "Created",
		cell: (pw) => <TimeLabel time={pw.createdAt} />,
	},
	{
		id: "arrow",
		header: "",
		className: "w-8",
		cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
	},
];

export const AllWithdrawalsList: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const [filters, setFilters] = useUrlState({
		status: { type: "string", default: "" },
		page: { type: "number", default: 1 },
		limit: { type: "number", default: 20 },
	});

	const offset = (filters.page - 1) * filters.limit;

	const { data, isLoading } = usePoolWithdrawals({
		status: filters.status || undefined,
		limit: filters.limit,
		offset,
	});

	const withdrawals = data?.withdrawals ?? [];
	const totalCount = data?.pagination.totalCount ?? 0;

	const handleRowClick = React.useCallback(
		(pw: PoolWithdrawal) => {
			const queryString = searchParams.toString();
			const targetPath = queryString
				? `/pool-withdrawals/${pw.id}?${queryString}`
				: `/pool-withdrawals/${pw.id}`;
			navigate(targetPath);
		},
		[navigate, searchParams],
	);

	return (
		<>
			<div className="space-y-4">
				<div className="flex gap-2 items-center">
					<Select
						value={filters.status || "all"}
						onValueChange={(v) =>
							setFilters({ status: v === "all" ? "" : v, page: 1 })
						}
					>
						<SelectTrigger className="w-[200px]">
							<SelectValue placeholder="Filter by status" />
						</SelectTrigger>
						<SelectContent>
							{STATUS_OPTIONS.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{filters.status && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setFilters({ status: "", page: 1 })}
						>
							Clear
						</Button>
					)}
				</div>

				<DataTable
					columns={columns}
					data={withdrawals}
					isLoading={isLoading}
					onRowClick={handleRowClick}
					getRowId={(row) => String(row.id)}
					emptyMessage="No withdrawals"
					emptyDescription={
						filters.status
							? "No withdrawals match the selected filter"
							: "Pool withdrawals will appear here"
					}
					emptyIcon={<Wallet className="h-8 w-8" />}
					pagination={
						totalCount > 0
							? {
									page: filters.page,
									pageSize: filters.limit,
									totalCount,
									onPageChange: (page: number) => setFilters({ page }),
									onPageSizeChange: (limit: number) =>
										setFilters({ limit, page: 1 }),
								}
							: undefined
					}
				/>
			</div>
			<Outlet />
		</>
	);
};
